/**
 * Validation that needs no time-lock library: event kinds, tag checks, signature
 * verification. Safe to import in the Worker without Node polyfills.
 */
import { verifyEvent, type Event } from "nostr-tools/pure";
import { base64 } from "@scure/base";
import { QUICKNET_CHAIN_HASH, currentRound, roundForTime, timeForRound } from "./drand.js";
import { ProfileError, SENDER_LIMITS, normalizeProfile, readSender, type SenderSnapshot } from "./sender.js";

export { QUICKNET_CHAIN_HASH, timeForRound, roundForTime, currentRound } from "./drand.js";

/** Nostr event kinds from NIP-XX Time Capsules and NIP-59. */
export const KIND_TIME_CAPSULE = 1041;
export const KIND_SEAL = 13;
export const KIND_GIFT_WRAP = 1059;

/** Pilot limit on the message text. NIP-XX allows up to 64 KiB. */
export const MAX_PLAINTEXT_BYTES = 16 * 1024;

/**
 * Private capsules are base64-encoded and padded twice more (seal, then wrap), which grows
 * 8 KiB of text to about 28 KB. Keeps every wrap under the API's 32 KiB event limit.
 */
export const MAX_PRIVATE_PLAINTEXT_BYTES = 8 * 1024;

export type CapsuleErrorCode =
  | "PLAINTEXT_TOO_LARGE"
  | "INVALID_ROUND"
  | "INVALID_RECIPIENT"
  | "INVALID_SIGNATURE"
  | "INVALID_CAPSULE"
  | "ROUND_NOT_REACHED"
  | "NOT_RECIPIENT"
  | "BEACON_UNAVAILABLE";

export class CapsuleError extends Error {
  constructor(
    public readonly code: CapsuleErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "CapsuleError";
  }
}

/** What a reader learns from a capsule before it unlocks. */
export type CapsuleHeader = {
  /** Author's public key, proven by signature. */
  author: string;
  chainHash: string;
  round: number;
  /** ms since epoch, derived from the round. */
  unlockAt: number;
  /** When the author created it, seconds since epoch (Nostr convention). */
  createdAt: number;
  /** The author's profile at sending time (PLAN.md 4.10), signed with the capsule; null if absent. */
  sender: SenderSnapshot | null;
  /** What the time lock holds: a manifest (PLAN.md 4.9) or, for v1 capsules, the text itself. */
  payload: Payload;
  /** Never encrypted (PLAN.md 4.14): readable by everyone who sees the capsule, before unlock too. Null on capsules made before subjects. */
  subject: string | null;
  /**
   * Optional teaser readable before unlock (PLAN.md 4.17): a plaintext tag on a public capsule, inside
   * the seal on a private one (the wrap never shows it). Null when the author wrote none.
   */
  intro: string | null;
  /**
   * Private capsules only (PLAN.md 4.16 K10): the sender asked that lists hide their name until the
   * recipient clicks the capsule. Inside the seal, so only the recipient sees it. Always false on a
   * public capsule.
   */
  discreet: boolean;
  /**
   * Reply envelope (PLAN.md 4.21): the sender asks for a private reply back to them, opening at
   * `unlockAt` (ms) or, when null, at a time the recipient chooses. Private capsules only; in the
   * rumor and on the wrap. Null when no reply is asked.
   */
  sase: ReplyEnvelope | null;
  /** Reply (PLAN.md 4.21): the id of the wrap this capsule answers. Private capsules only. */
  replyTo: string | null;
  /** CC (PLAN.md 4.21): who else received this capsule, the sender's snapshot. Private capsules only. */
  recipients: Recipients | null;
};

/** The reply envelope of a capsule: when the reply must open, or null for the recipient's choice. */
export type ReplyEnvelope = { unlockAt: number | null };

/** A recipient named in a capsule: key and the display name the sender saw (PLAN.md 4.21). */
export type NamedKey = { pubkey: string; display_name: string };

/** The `to` and `cc` lists of a capsule, in tag order. */
export type Recipients = { to: NamedKey[]; cc: NamedKey[] };

/** Most keys in the `to` and `cc` tags together (the group send limit of PLAN.md 4.14). */
export const MAX_RECIPIENT_TAGS = 20;

const HEX_KEY = /^[0-9a-f]{64}$/;
const UNIX_SECONDS = /^[1-9][0-9]{0,11}$/;

/**
 * The "sase" tag (PLAN.md 4.21): absent, one bare tag (the recipient chooses the reply's unlock
 * time), or one tag with the unlock time in unix seconds, which must be a round's own time between
 * round 1 and MAX_ROUND so that a reply locked to that time matches it exactly.
 */
export function readSase(event: Pick<Event, "tags">): ReplyEnvelope | null {
  const tags = event.tags.filter((t) => t[0] === "sase");
  if (tags.length === 0) return null;
  if (tags.length !== 1 || tags[0].length > 2) throw new CapsuleError("INVALID_CAPSULE", "one sase tag, bare or with unix seconds");
  if (tags[0].length === 1) return { unlockAt: null };
  if (!UNIX_SECONDS.test(tags[0][1])) throw new CapsuleError("INVALID_CAPSULE", "the sase tag's time is unix seconds");
  const unlockAt = Number(tags[0][1]) * 1000;
  const round = roundForTime(unlockAt);
  if (round < 1 || round > MAX_ROUND || timeForRound(round) !== unlockAt) {
    throw new CapsuleError("INVALID_CAPSULE", "the sase tag's time must be a drand round's time, before 2126-01-01");
  }
  return { unlockAt };
}

/** The "reply" tag (PLAN.md 4.21): absent, or one tag with the id of the wrap being answered. */
export function readReplyTo(event: Pick<Event, "tags">): string | null {
  const tags = event.tags.filter((t) => t[0] === "reply");
  if (tags.length === 0) return null;
  if (tags.length !== 1 || tags[0].length !== 2 || !HEX_KEY.test(tags[0][1])) {
    throw new CapsuleError("INVALID_CAPSULE", "one reply tag with the id of the wrap it answers");
  }
  return tags[0][1];
}

/**
 * The "to" and "cc" tags (PLAN.md 4.21): absent, or `[to|cc, <pubkey>, <display name>]` each, every
 * key once across both, at most MAX_RECIPIENT_TAGS in total, names as in the sender snapshot.
 */
export function readRecipients(event: Pick<Event, "tags">): Recipients | null {
  const tags = event.tags.filter((t) => t[0] === "to" || t[0] === "cc");
  if (tags.length === 0) return null;
  if (tags.length > MAX_RECIPIENT_TAGS) throw new CapsuleError("INVALID_CAPSULE", `at most ${MAX_RECIPIENT_TAGS} recipients in to and cc`);
  const seen = new Set<string>();
  const out: Recipients = { to: [], cc: [] };
  for (const t of tags) {
    if (t.length !== 3 || !HEX_KEY.test(t[1])) throw new CapsuleError("INVALID_CAPSULE", `a ${t[0]} tag has the form [${t[0]}, pubkey, display name]`);
    if (seen.has(t[1])) throw new CapsuleError("INVALID_CAPSULE", "a recipient is named twice in to and cc");
    seen.add(t[1]);
    let display_name: string;
    try {
      display_name = normalizeProfile({ display_name: t[2] }).display_name;
    } catch (err) {
      throw new CapsuleError("INVALID_CAPSULE", `${t[0]} name: ${err instanceof ProfileError ? err.message : String(err)}`);
    }
    if (display_name !== t[2]) throw new CapsuleError("INVALID_CAPSULE", `a ${t[0]} name must be trimmed, at most ${SENDER_LIMITS.display_name} characters`);
    out[t[0] as "to" | "cc"].push({ pubkey: t[1], display_name });
  }
  return out;
}

/** Throws INVALID_CAPSULE when a capsule that must be public carries a private-only tag (PLAN.md 4.21). */
export function assertNoPrivateTags(event: Pick<Event, "tags">): void {
  if (readSase(event) !== null) throw new CapsuleError("INVALID_CAPSULE", "only a private capsule can ask for a reply");
  if (readReplyTo(event) !== null) throw new CapsuleError("INVALID_CAPSULE", "only a private capsule can be a reply");
  if (readRecipients(event) !== null) throw new CapsuleError("INVALID_CAPSULE", "only a private capsule names its recipients");
}

/** Longest subject, in characters (PLAN.md 4.14). */
export const MAX_SUBJECT_CHARS = 200;

/** One line: control characters and runs of white space become one space; trimmed. */
export function normalizeSubject(value: string): string {
  return value.replace(/\p{Cc}|\p{Zl}|\p{Zp}/gu, " ").replace(/\s+/g, " ").trim();
}

/** Throws INVALID_CAPSULE unless `value` is a normalized subject of 1 to 200 characters. */
export function assertSubject(value: unknown): string {
  if (typeof value !== "string" || value !== normalizeSubject(value) || value.length === 0 || [...value].length > MAX_SUBJECT_CHARS) {
    throw new CapsuleError("INVALID_CAPSULE", `subject must be one line of 1 to ${MAX_SUBJECT_CHARS} characters`);
  }
  return value;
}

/** The "subject" tag: at most one, well formed; null when absent. */
export function readSubject(event: Pick<Event, "tags">): string | null {
  const tags = event.tags.filter((t) => t[0] === "subject");
  if (tags.length === 0) return null;
  if (tags.length !== 1 || tags[0].length !== 2) throw new CapsuleError("INVALID_CAPSULE", "one subject tag with one value");
  return assertSubject(tags[0][1]);
}

/** Longest intro, in characters (PLAN.md 4.17). */
export const MAX_INTRO_CHARS = 200;

/**
 * Plain text that keeps line breaks: CR LF and CR become LF, other control and separator characters a
 * space; each line has single spaces and is trimmed; at most one empty line in a row; trimmed.
 */
export function normalizeIntro(value: string): string {
  return value
    .replace(/\r\n?/g, "\n")
    .replace(/\p{Cc}|\p{Zl}|\p{Zp}|\s/gu, (c) => (c === "\n" ? c : " "))
    .split("\n")
    .map((line) => line.replace(/ +/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Throws INVALID_CAPSULE unless `value` is a normalized intro of 1 to 200 characters. */
export function assertIntro(value: unknown): string {
  if (typeof value !== "string" || value !== normalizeIntro(value) || value.length === 0 || [...value].length > MAX_INTRO_CHARS) {
    throw new CapsuleError("INVALID_CAPSULE", `intro must be plain text of 1 to ${MAX_INTRO_CHARS} characters`);
  }
  return value;
}

/** The "intro" tag: at most one, well formed; null when absent. */
export function readIntro(event: Pick<Event, "tags">): string | null {
  const tags = event.tags.filter((t) => t[0] === "intro");
  if (tags.length === 0) return null;
  if (tags.length !== 1 || tags[0].length !== 2) throw new CapsuleError("INVALID_CAPSULE", "one intro tag with one value");
  return assertIntro(tags[0][1]);
}

/**
 * What anyone who holds a private wrap sees without a key (PLAN.md 4.14): recipient, subject, unlock
 * time (ms); since PLAN.md 4.21 also the reply envelope and the id of the wrap it answers.
 */
export type WrapHeader = { recipient: string; subject: string | null; unlockAt: number | null; sase: ReplyEnvelope | null; replyTo: string | null };

/**
 * Reads the plaintext tags of a kind 1059 wrap. The sender writes them; unwrapPrivate checks them
 * against the signed inner capsule, so a wrong subject or time is caught when the recipient opens it.
 */
export function readWrapHeader(wrap: Pick<Event, "kind" | "tags">): WrapHeader {
  if (wrap.kind !== KIND_GIFT_WRAP) throw new CapsuleError("INVALID_CAPSULE", `expected kind ${KIND_GIFT_WRAP}`);
  const p = wrap.tags.filter((t) => t[0] === "p");
  if (p.length !== 1 || !/^[0-9a-f]{64}$/.test(p[0][1] ?? "")) throw new CapsuleError("INVALID_CAPSULE", "exactly one p tag with a pubkey");
  const unlock = wrap.tags.filter((t) => t[0] === "unlock");
  let unlockAt: number | null = null;
  if (unlock.length > 0) {
    if (unlock.length !== 1 || unlock[0].length !== 2 || !/^[1-9][0-9]{0,11}$/.test(unlock[0][1])) {
      throw new CapsuleError("INVALID_CAPSULE", "one unlock tag with unix seconds");
    }
    unlockAt = Number(unlock[0][1]) * 1000;
  }
  return { recipient: p[0][1], subject: readSubject(wrap), unlockAt, sase: readSase(wrap), replyTo: readReplyTo(wrap) };
}

export type Payload = "text" | "manifest-v1";

/** The payload tag: none means v1 text; "manifest-v1" means a JSON manifest; anything else is unreadable. */
export function readPayload(event: Pick<Event, "tags">): Payload {
  const tags = event.tags.filter((t) => t[0] === "payload");
  if (tags.length === 0) return "text";
  if (tags.length === 1 && tags[0].length === 2 && tags[0][1] === "manifest-v1") return "manifest-v1";
  throw new CapsuleError("INVALID_CAPSULE", "unsupported payload tag");
}

/** The "discreet" tag of an inner capsule: absent, or exactly one bare tag. */
export function readDiscreet(event: Pick<Event, "tags">): boolean {
  const tags = event.tags.filter((t) => t[0] === "discreet");
  if (tags.length === 0) return false;
  if (tags.length === 1 && tags[0].length === 1) return true;
  throw new CapsuleError("INVALID_CAPSULE", "a discreet capsule carries one bare discreet tag");
}

/** Sender snapshot of a capsule; a malformed one makes the capsule INVALID_CAPSULE. */
export function readCapsuleSender(event: Pick<Event, "tags">): SenderSnapshot | null {
  try {
    return readSender(event);
  } catch (err) {
    throw new CapsuleError("INVALID_CAPSULE", `sender snapshot: ${err instanceof ProfileError ? err.message : String(err)}`);
  }
}

/**
 * Latest round a capsule may name: the last one before 2126-01-01 00:00 UTC (PLAN.md 4.11 H7).
 * Larger rounds have unlock times outside what dates and the UI can show.
 */
export const MAX_ROUND = currentRound(Date.UTC(2126, 0, 1));

export function assertRound(round: number): void {
  if (!Number.isSafeInteger(round) || round < 1) {
    throw new CapsuleError("INVALID_ROUND", `round must be a positive integer, got ${String(round)}`);
  }
  if (round > MAX_ROUND) throw new CapsuleError("INVALID_ROUND", `round ${round} is after 2126-01-01, the latest allowed unlock time`);
}

const AGE_VERSION = "age-encryption.org/v1";
const MAC_LINE = [0x0a, 0x2d, 0x2d, 0x2d, 0x20]; // "\n--- "

/**
 * Reads the recipient stanza of the age file inside a capsule (`content`, base64 of age v1
 * binary). Requires exactly one stanza, `-> tlock <round> <chain hash>`. The stanza, not the
 * tag, decides which beacon decrypts the capsule, so a reader checks that they agree (H7).
 */
export function readStanza(contentBase64: string): { chainHash: string; round: number } {
  let bytes: Uint8Array;
  try {
    bytes = base64.decode(contentBase64);
  } catch {
    throw new CapsuleError("INVALID_CAPSULE", "content is not valid base64");
  }
  // The header is text up to the MAC line; the payload after it is binary and never parsed here.
  let end = -1;
  for (let i = 0; i + MAC_LINE.length <= bytes.length && i < 64 * 1024; i++) {
    if (MAC_LINE.every((b, j) => bytes[i + j] === b)) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new CapsuleError("INVALID_CAPSULE", "age header has no MAC line");
  let header = "";
  for (let i = 0; i < end; i++) header += String.fromCharCode(bytes[i]);
  const lines = header.split("\n");
  if (lines[0] !== AGE_VERSION) throw new CapsuleError("INVALID_CAPSULE", "content is not an age v1 file");
  const stanzas = lines.slice(1).filter((l) => l.startsWith("->"));
  if (stanzas.length !== 1) throw new CapsuleError("INVALID_CAPSULE", `exactly one recipient stanza is required, found ${stanzas.length}`);
  const [arrow, type, roundStr, chainHash, ...rest] = stanzas[0].split(" ");
  if (arrow !== "->" || type !== "tlock" || rest.length > 0 || !chainHash) {
    throw new CapsuleError("INVALID_CAPSULE", "the recipient stanza is not a tlock stanza");
  }
  if (!/^[1-9][0-9]*$/.test(roundStr ?? "")) throw new CapsuleError("INVALID_CAPSULE", "tlock stanza round is invalid");
  return { chainHash, round: Number(roundStr) };
}

/**
 * Reads and validates the single tlock tag of a kind 1041 event and checks that the age stanza
 * in `content` names the same round and chain. Every reader (API, browser, offline opener)
 * goes through here, so a capsule cannot claim one unlock time and open at another.
 */
export function readCapsuleTag(event: Pick<Event, "kind" | "tags" | "content">): { chainHash: string; round: number } {
  if (event.kind !== KIND_TIME_CAPSULE) {
    throw new CapsuleError("INVALID_CAPSULE", `expected kind ${KIND_TIME_CAPSULE}, got ${event.kind}`);
  }
  const tags = event.tags.filter((t) => t[0] === "tlock");
  if (tags.length !== 1) throw new CapsuleError("INVALID_CAPSULE", "exactly one tlock tag is required");
  const [, chainHash, roundStr] = tags[0];
  if (chainHash !== QUICKNET_CHAIN_HASH) {
    throw new CapsuleError("INVALID_CAPSULE", "tlock chain hash is not quicknet");
  }
  if (!/^[1-9][0-9]*$/.test(roundStr ?? "")) throw new CapsuleError("INVALID_CAPSULE", "tlock round is invalid");
  const round = Number(roundStr);
  assertRound(round);
  const stanza = readStanza(event.content);
  if (stanza.round !== round || stanza.chainHash !== chainHash) {
    throw new CapsuleError("INVALID_CAPSULE", `tlock tag says round ${round}, the ciphertext is locked to round ${stanza.round}`);
  }
  return { chainHash, round };
}

/** Plain copy with only the signed fields, so a cached "verified" marker from nostr-tools is never trusted. */
function plainEvent(event: Event): Event {
  const { id, pubkey, created_at, kind, tags, content, sig } = event;
  return { id, pubkey, created_at, kind, tags, content, sig };
}

/** Verifies id and signature and returns the author. Works before the unlock time. */
export function verifyAuthor(event: Event): string {
  if (!verifyEvent(plainEvent(event))) throw new CapsuleError("INVALID_SIGNATURE", "event signature is invalid");
  return event.pubkey;
}

/** Header of a signed public capsule: author, round, unlock time. Throws on bad signature or tags. */
export function readPublicHeader(event: Event): CapsuleHeader {
  const author = verifyAuthor(event);
  const { chainHash, round } = readCapsuleTag(event);
  assertNoPrivateTags(event);
  return {
    author,
    chainHash,
    round,
    unlockAt: timeForRound(round),
    createdAt: event.created_at,
    sender: readCapsuleSender(event),
    payload: readPayload(event),
    subject: readSubject(event),
    intro: readIntro(event),
    discreet: false,
    sase: null,
    replyTo: null,
    recipients: null,
  };
}

